import { MessageSeverity } from "primereact/api";

import {
  ChatApi,
  ChatExchangeModel,
  ChatPostRequest,
  ChatStreamPostRequest,
  ChatThreadModel,
  ChatTurn,
  KnowledgeBaseReadChatbot,
  KnowledgeBaseSelection,
  ResponseError,
} from "../../../@api";

import { applicationConfig } from "../../../environmentConfiguration";
import { translationKeys } from "../../models/app-translation-keys";
import { IChatThreadExtendedModel } from "../../models/interfaces";
import { ICustomKnowledgeBaseReadChatbot } from "../../stores";
import { IStoreContext } from "../../stores/StoreContext";
import { ChatErrorResponseHandlingMiddleware } from "../ApiMiddlewares";
import { ChatApiService } from "../ApiServices/ChatApiService";
import { GenericService } from "../GenericService";
import { ThreadService } from "./ThreadService";

const LOCAL_STORAGE_KEY_SELECTED_KNOWLEDGEBASE = "selectedKnowledgeBase";

export class ChatService extends GenericService<ChatApi, ChatApiService> {
  constructor(storeContext: IStoreContext) {
    super(storeContext, ChatApi, ChatApiService);
  }

  protected override get afterGlobalMiddlewares() {
    return [new ChatErrorResponseHandlingMiddleware(this.storeContext.userStore)];
  }

  public async sendChatMessage(
    requestThread: IChatThreadExtendedModel | undefined,
    message: string,
    isAnonymousMode: boolean,
    knowledgeBase: KnowledgeBaseSelection | undefined,
    modelId: string,
  ): Promise<ChatExchangeModel | undefined> {
    const { threadStore, userStore, settingsStore, streamingChatStore } = this.storeContext;

    if (!message?.length) {
      console.warn(`MessageInput was empty: ${message}`);
      return;
    }
    threadStore.threadErrors = [];

    const history =
      requestThread?.exchanges?.slice().map(e => ({ fromUser: e.prompt, fromBot: e.answer }) as ChatTurn) ?? [];
    history.push({
      fromUser: message,
      fromBot: null,
      combinedCharacterCount: message.length,
    });

    let pendingThread: IChatThreadExtendedModel = {} as IChatThreadExtendedModel;
    pendingThread = { ...requestThread, pendingPrompt: message, modelId } as IChatThreadExtendedModel;

    try {
      const requestPayload: ChatStreamPostRequest | ChatPostRequest = {
        knowledgeChatRequestDto: {
          isAnonymousMode,
          selectedKnowledgeBase: knowledgeBase,
          request: {
            history,
            modelId,
            threadId: requestThread?.id,
            overrides: {
              searchSettingsOverrides: {},
              chatbotSettingsOverrides: {},
              responseLanguage: userStore.currentLanguage.code,
            },
          },
        },
      };

      let response: ChatThreadModel | undefined = undefined;
      if (applicationConfig.featureFlags.enableStreamingChat) {
        threadStore.setIsExchangeCancelledInThread(requestThread?.id, false);

        const abortController = new AbortController();
        streamingChatStore.startStreaming(message);
        streamingChatStore.registerStopCallback(() => {
          pendingThread = { ...pendingThread, isExchangeCancelled: true };
          threadStore.updateOrCreatePendingThread(pendingThread);

          abortController.abort();
        });

        response = await this.apiService.chatStreamPost(requestPayload, { signal: abortController.signal });
        const exchanges = pendingThread.exchanges ?? [];
        const latestExchange = exchanges.length > 0 ? exchanges[exchanges.length - 1] : undefined;
        streamingChatStore.finalizeStreaming(latestExchange?.id);
      } else {
        response = await this.apiService.chatPost(requestPayload);
      }

      pendingThread = {
        ...pendingThread,
        ...response,
      };
    } catch (error: any) {
      if (applicationConfig.featureFlags.enableStreamingChat) {
        const isAbort = error?.name === "AbortError" || error?.cause?.name === "AbortError";
        if (isAbort) {
          // User clicked Stop Generating — restore the question to the input field for quick retry.
          threadStore.triggerCopyResponseToInput(message);
          return;
        }
        streamingChatStore.reset();
      }

      if (error.constructor.name === "ResponseError") {
        const responseError = error as ResponseError;
        const response = responseError.response;

        if (response.status === 422) {
          threadStore.triggerCopyResponseToInput(message);
          threadStore.threadErrors = [
            {
              messageTranslationKey: translationKeys.thread.error.contentFilter,
              messageDescriptionTranslationKey: translationKeys.thread.error.contentFilterDescription,
              isDescriptionMarkdown: true,
              severity: MessageSeverity.WARN,
            },
          ];

          return;
        }

        console.warn("unexpected status", response.status, await response.text());
        return;
      }

      console.error("unexpected error", error);
    } finally {
      pendingThread = { ...pendingThread, pendingPrompt: undefined };
      const activeThread = threadStore.activeThread;

      // NOTE #63781 if a user is on NewChat ((activeThread?.id is undefined and activeThread?.pendingPrompt) or AnonymousMode is set for the request thread), set the activeThread to the pendingThread after the response (with the created/new threadId) is received
      if ((!activeThread?.id && !!activeThread?.pendingPrompt) || isAnonymousMode) {
        threadStore.setActiveThread(pendingThread);
      }

      threadStore.updateOrCreatePendingThread(pendingThread);

      if (settingsStore.persistAnyActions) {
        void new ThreadService(this.storeContext).loadThreads();
      }
    }
  }

  public async setSelectedKnowledgeBase(knowledgeBase: KnowledgeBaseReadChatbot): Promise<void> {
    this.storeContext.chatStore.selectedKnowledgeBase = knowledgeBase;
    this.setSelectedKnowledgeBaseToLocalStorage(knowledgeBase);
  }

  public getKnowledgeBases(): Promise<KnowledgeBaseReadChatbot[]> {
    return this.apiService.chatGetKnowledgeBases({});
  }

  public getSelectedKnowledgeBaseFromLocalStorage(): ICustomKnowledgeBaseReadChatbot | undefined {
    const selectedKnowledgeBase = localStorage.getItem(LOCAL_STORAGE_KEY_SELECTED_KNOWLEDGEBASE);
    if (selectedKnowledgeBase) {
      return JSON.parse(selectedKnowledgeBase) as ICustomKnowledgeBaseReadChatbot;
    } else {
      return undefined;
    }
  }

  public setSelectedKnowledgeBaseToLocalStorage(knowledgeBase: ICustomKnowledgeBaseReadChatbot): void {
    localStorage.setItem(LOCAL_STORAGE_KEY_SELECTED_KNOWLEDGEBASE, JSON.stringify(knowledgeBase));
  }

  public downloadChatSourceBlob(chatSourceId: string): Promise<Blob> {
    return this.apiService.chatDownloadBlobKnowledgebaseBlobGet({ chatSourceId });
  }
}
